import { useEffect } from "react";
import { useBlocker } from "react-router-dom";
import { Button } from "@/components/ax/Button";
import { PrimaryBar } from "@/components/ax/PrimaryBar";
import { Sheet } from "@/components/ax/Sheet";
import { StepAxis } from "@/components/intake/StepAxis";
import { StepItems } from "@/components/intake/StepItems";
import { StepNotes } from "@/components/intake/StepNotes";
import { StepOptions } from "@/components/intake/StepOptions";
import { StepQuestion } from "@/components/intake/StepQuestion";
import { useIntake } from "@/hooks/useIntake";
import { useI18n } from "@/i18n";
import { getFlags } from "@/lib/flags";

function usePrimary(s, t) {
  const { step, busy } = s;
  const locked = getFlags().locked;
  if (step === 1) {
    return { label: busy === "draft" ? t("intake.saving") : t("intake.next"), disabled: !!busy || locked || s.resumeState !== "ready", run: s.submitQuestion };
  }
  if (step === 2) {
    const canParse = s.note.trim() && s.aiOn && s.left > 0 && s.parsedNote !== s.note;
    if (canParse) {
      return { label: busy === "parse" ? t("notes.parsing") : busy === "note" ? t("notes.saving") : t("notes.parse"), disabled: !!busy, run: s.parse };
    }
    return { label: busy === "note" ? t("notes.saving") : t("notes.toItems"), disabled: !!busy, run: s.toItems };
  }
  if (step === 3) return { label: t("intake.next"), disabled: !s.hasKA || !!busy, run: () => s.setStep(4) };
  const useAi = s.aiOn && s.left > 0;
  return {
    label: busy === "compose" ? t("brief.composing") : useAi ? t("brief.compose") : t("brief.skip"),
    disabled: s.options.length < 2 || !!busy,
    run: () => s.finish(useAi),
  };
}

export default function KeputusanBaru() {
  const { t } = useI18n();
  const s = useIntake();
  const primary = usePrimary(s, t);
  const blocker = useBlocker(({ currentLocation, nextLocation }) => s.dirty && currentLocation.pathname !== nextLocation.pathname);

  useEffect(() => {
    if (!s.dirty) return undefined;
    const warn = (e) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [s.dirty]);

  return (
    <div data-testid="intake-page">
      {s.step > 1 && (
        <Button
          variant="ghost"
          onClick={() => s.setStep(s.step - 1)}
          data-testid="intake-back-button"
          className="-ml-0 mb-2 px-0"
        >
          {t("intake.back")}
        </Button>
      )}
      <h1 className="type-h1">{t("decision.new")}</h1>
      {getFlags().locked && (
        <p data-testid="intake-locked" className="m-0 mt-2 text-sm leading-5 text-ax-text-2">
          {t("locked")}
        </p>
      )}
      <StepAxis step={s.step} />
      {s.resumeState === "loading" && <p role="status" className="mt-6">{t("auth.checking")}</p>}
      {["error", "missing"].includes(s.resumeState) && <div role="alert" className="mt-6">
        <p>{t(s.resumeState === "missing" ? "ws.notFound" : "err.load")}</p>
        <Button onClick={s.retryResume}>{t("retry")}</Button>
      </div>}
      {s.step === 1 && s.resumeState === "ready" && <StepQuestion s={s} />}
      {s.step === 2 && <StepNotes s={s} />}
      {s.step === 3 && <StepItems s={s} />}
      {s.step === 4 && <StepOptions s={s} />}

      {s.errors.form && (
        <p role="alert" data-testid="intake-form-error" className="m-0 mt-4 text-sm leading-5 text-ax-error">
          {s.errors.form}
        </p>
      )}
      <PrimaryBar>
        <Button
          variant="primary"
          busy={!!s.busy}
          disabled={primary.disabled}
          onClick={primary.run}
          data-testid="intake-primary-button"
          className="w-full md:w-auto md:min-w-[200px]"
        >
          {primary.label}
        </Button>
      </PrimaryBar>

      <Sheet
        open={blocker.state === "blocked"}
        onOpenChange={(o) => !o && blocker.reset?.()}
        title={t("dirty.title")}
        testId="dirty-sheet"
      >
        <Button variant="primary" onClick={() => blocker.reset()} data-testid="dirty-stay-button" className="w-full">
          {t("dirty.stay")}
        </Button>
        <Button onClick={() => blocker.proceed()} data-testid="dirty-discard-button" className="mt-2 w-full">
          {t("dirty.discard")}
        </Button>
      </Sheet>
    </div>
  );
}
